import React, { useState, useCallback, useEffect } from 'react';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '../components/TextField';
import apiClient from '../utils/apiClient';

interface AddressComponent {
  types: string[];
  long_name: string;
  short_name: string;
}

interface PredictionResult {
  description: string;
  place_id: string;
  address_components: AddressComponent[];
  formatted_address: string;
  geometry?: {
    location: {
      lat: number;
      lng: number;
    };
  };
}

interface Coordinates {
  lat: number;
  lng: number;
}

const MapsAutocomplete = ({ onChange = (args) => {} }) => {
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState<PredictionResult[]>([]);
  const [loading, setLoading] = useState(false);

  const searchAddresses = useCallback(
    async (query: string) => {
      if (!query.trim() || query.length < 2) {
        setSuggestions([]);
        return;
      } else {
        setLoading(true);
      }

      try {
        const response = await apiClient.get('/geocode/autocomplete', { input: query });
        
        const predictions = response?.predictions || [];
        setSuggestions(predictions);
      } catch (error) {
        console.error('Error fetching address predictions:', error);
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchAddresses(inputValue);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [inputValue, searchAddresses]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  const handleSelect = async (event: any, suggestion: PredictionResult | null) => {
    if (!suggestion) return;

    try {
      let coordinates: Coordinates;
      
      if (suggestion.geometry?.location) {
        coordinates = {
          lat: suggestion.geometry.location.lat,
          lng: suggestion.geometry.location.lng
        };
      } else {
         const parts = suggestion.description.split(', ');
         const streetAddress = parts[0] || '';
         const city = parts[1] || '';
         const state = parts[2] || '';
         const country = parts[3] || '';
         
         const geocodeResult = await apiClient.post('/geocode/coordinates', {
           streetAddress,
           addressLine2: null,
           city,
           state,
           country,
           zip: ''
         });
         coordinates = {
           lat: geocodeResult.lat,
           lng: geocodeResult.lng
         };
       }

      let streetAddress = '';
      let city = '';
      let state = '';
      let country = '';

      if (suggestion.address_components) {
        suggestion.address_components.forEach(component => {
          const type = component.types[0];
          switch (type) {
            case 'street_number':
              streetAddress = component.long_name;
              break;
            case 'route':
              streetAddress = streetAddress ? `${streetAddress} ${component.long_name}` : component.long_name;
              break;
            case 'locality':
            case 'administrative_area_level_2':
              if (!city) city = component.long_name;
              break;
            case 'administrative_area_level_1':
              state = component.long_name;
              break;
            case 'country':
              country = component.long_name;
              break;
          }
        });
      } else {
        const parts = suggestion.description.split(', ');
        if (parts.length > 0) streetAddress = parts[0];
        if (parts.length > 1) city = parts[1];
        if (parts.length > 2) state = parts[2];
        if (parts.length > 3) country = parts[3];
      }

      const payload = {
        coordinates,
        name: '',
        fullAddress: suggestion.description,
        streetAddress,
        components: {
          streetAddress,
          city,
          state,
          country,
        },
        originalObject: {
          ...suggestion,
          lat: coordinates.lat,
          lng: coordinates.lng,
        },
      };

      onChange(payload);
    } catch (error) {
      console.error('Error processing selected address:', error);
    }
  };

  return (
    <div>
      <Autocomplete
        id="credits-autocomplete"
        options={suggestions}
        fullWidth
        loading={loading}
        getOptionLabel={(suggestion: PredictionResult) => suggestion?.description || suggestion?.formatted_address || ''}
        onChange={handleSelect}
        inputValue={inputValue}
        onInputChange={(event, newValue) => {
          setInputValue(newValue);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            onChange={handleInputChange}
            variant="outlined"
            inputProps={{
              ...params.inputProps,
              id: 'location-search-input',
              placeholder: "Look up address..."
            }}
          />
        )}
        noOptionsText={loading ? "Loading..." : "No addresses found"}
        filterOptions={(options) => options}
      />
    </div>
  );
};

export default MapsAutocomplete;
